import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { useApp } from "./AppContext";
import { API_CONFIG, getApiUrl } from "../config/api";
import { useSalon } from "./SalonContext";
import { useFeatureAccess } from "./FeatureAccessContext";
import moment from "moment";
import { createSearchIndex, processClientData } from "../utils/clientSearch";

const CampaignContext = createContext();

export function CampaignProvider({ children }) {
  const { fetchWithAuth } = useApp();
  const { businessInfo } = useSalon();
  const { hasFeature } = useFeatureAccess();
  // Only send client_group_ids when the feature is enabled for this env/salon.
  // The backend companion (client-group targeting) is dark until the flag flips
  // — gating the key keeps campaign create/compliance payloads byte-for-byte
  // unchanged where the feature is off (e.g. prod), so this never affects the
  // existing explicit-client path.
  const clientGroupsEnabled = hasFeature("client_groups");

  // Client list (fetched independently so all clients are available, not just a paginated subset)
  const [allClients, setAllClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsError, setClientsError] = useState(null);

  const fetchAllClients = useCallback(async () => {
    if (!businessInfo?.id) return;
    try {
      setClientsLoading(true);
      setClientsError(null);
      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SALON_CLIENTS, "", {
          salonId: businessInfo.id,
        }),
      );
      if (!response?.clients)
        throw new Error("Invalid response format from server");
      setAllClients(response.clients);
    } catch (err) {
      setClientsError(err.message || "Failed to fetch clients");
      setAllClients([]);
    } finally {
      setClientsLoading(false);
    }
  }, [fetchWithAuth, businessInfo?.id]);

  useEffect(() => {
    if (businessInfo?.id) {
      fetchAllClients();
    } else {
      setAllClients([]);
    }
  }, [businessInfo?.id, fetchAllClients]);

  // Step navigation
  const [step, setStep] = useState(1);

  // Campaign setup
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(false);
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState(
    moment().format("YYYY-MM-DD"),
  );
  const [scheduledTime, setScheduledTime] = useState("");
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  // Client-group UUIDs the campaign targets; members are resolved live at send
  // time by the backend (see marketing/create_campaign + dispatch_campaign).
  const [selectedClientGroupIds, setSelectedClientGroupIds] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [genderFilter, setGenderFilter] = useState("");
  const [ageGroupFilter, setAgeGroupFilter] = useState("");
  const [clientPage, setClientPage] = useState(1);
  const [sorting, setSorting] = useState({
    field: "last_name",
    direction: "asc",
  });

  // Create search index for client selection
  const searchIndex = useMemo(() => {
    return createSearchIndex(allClients);
  }, [allClients]);

  // Process clients based on search, filters, and sorting
  const processedClients = useMemo(() => {
    return processClientData({
      clients: allClients,
      searchIndex,
      searchQuery: clientSearch,
      filters: {
        genderFilter,
        ageGroupFilter,
        getAge: (birthday) => {
          if (!birthday) return null;
          const diff = Date.now() - new Date(birthday).getTime();
          return Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
        },
        getAgeGroup: (age) => {
          if (age == null) return null;
          if (age < 25) return "under25";
          if (age < 35) return "25-34";
          if (age < 45) return "35-44";
          if (age < 55) return "45-54";
          if (age < 65) return "55-64";
          return "65plus";
        },
      },
      sorting,
    });
  }, [
    allClients,
    searchIndex,
    clientSearch,
    genderFilter,
    ageGroupFilter,
    sorting,
  ]);

  // Message creation
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [message, setMessage] = useState("");
  const [templateVariables, setTemplateVariables] = useState([]);
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [complianceMessages, setComplianceMessages] = useState([]);

  // Consolidated loading and error states
  const [loadingState, setLoadingState] = useState({
    templateVars: false,
    templates: false,
    complianceMessages: false,
  });

  const [apiErrors, setApiErrors] = useState({
    templateVars: null,
    templates: null,
    complianceMessages: null,
  });

  // For tracking if data is loaded (caching)
  const [marketingDataLoaded, setMarketingDataLoaded] = useState(false);

  // Compliance
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [complianceStatus, setComplianceStatus] = useState(null);
  const [complianceErrors, setComplianceErrors] = useState([]);
  const [complianceWarnings, setComplianceWarnings] = useState([]);

  // Campaign Creation
  const [campaignCreationLoading, setCampaignCreationLoading] = useState(false);
  const [campaignCreationError, setCampaignCreationError] = useState(null);

  // Submission
  const [submitted, setSubmitted] = useState(false);

  // Discard Confirmation Modal
  const [showDiscardModal, setShowDiscardModal] = useState(false);

  // Campaign list and details state
  const [campaigns, setCampaigns] = useState([]);
  const [campaignDetails, setCampaignDetails] = useState(null);
  const [campaignMessages, setCampaignMessages] = useState([]);
  const [campaignLoading, setCampaignLoading] = useState({
    list: false,
    details: false,
    messages: false,
    update: false,
    delete: false,
  });
  const [campaignErrors, setCampaignErrors] = useState({
    list: null,
    details: null,
    messages: null,
    update: null,
    delete: null,
  });

  // Helper functions for loading and error state
  const setLoading = useCallback((resource, isLoading) => {
    setLoadingState((prev) => ({ ...prev, [resource]: isLoading }));
  }, []);

  const setApiError = useCallback((resource, error) => {
    setApiErrors((prev) => ({ ...prev, [resource]: error }));
  }, []);

  // Fetch template variables
  const fetchTemplateVariables = useCallback(async () => {
    if (!businessInfo?.id) {
      return;
    }

    try {
      setLoading("templateVars", true);
      setApiError("templateVars", null);

      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.MARKETING_TEMPLATE_VARIABLES, "", {
          salonId: businessInfo?.id,
        }),
      );

      if (response) {
        setTemplateVariables(response);
      }
    } catch (err) {
      setApiError(
        "templateVars",
        err.message || "Failed to fetch template variables",
      );
      setTemplateVariables([]);
    } finally {
      setLoading("templateVars", false);
    }
  }, [businessInfo?.id, fetchWithAuth, setLoading, setApiError]);

  // Fetch message templates
  const fetchMessageTemplates = useCallback(async () => {
    try {
      setLoading("templates", true);
      setApiError("templates", null);

      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.MARKETING_TEMPLATES),
      );

      if (response) {
        setMessageTemplates(response);
      }
    } catch (err) {
      setApiError(
        "templates",
        err.message || "Failed to fetch message templates",
      );
      setMessageTemplates([]);
    } finally {
      setLoading("templates", false);
    }
  }, [fetchWithAuth, setLoading, setApiError]);

  // Fetch compliance messages
  const fetchComplianceMessages = useCallback(async () => {
    try {
      setLoading("complianceMessages", true);
      setApiError("complianceMessages", null);

      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.MARKETING_COMPLIANCE_MESSAGES),
      );

      if (response) {
        setComplianceMessages(response);
      }
    } catch (err) {
      setApiError(
        "complianceMessages",
        err.message || "Failed to fetch compliance messages",
      );
      setComplianceMessages([]);
    } finally {
      setLoading("complianceMessages", false);
    }
  }, [fetchWithAuth, setLoading, setApiError]);

  // Fetch all marketing data at once (template variables, message templates, compliance messages)
  const fetchMarketingData = useCallback(async () => {
    if (!businessInfo?.id || marketingDataLoaded) {
      return; // Skip if no salon ID or data already loaded (simple caching)
    }

    try {
      // Set all loading states
      setLoading("templateVars", true);
      setLoading("templates", true);
      setLoading("complianceMessages", true);

      // Clear all error states
      setApiError("templateVars", null);
      setApiError("templates", null);
      setApiError("complianceMessages", null);

      // Execute all API calls in parallel with individual error handling
      const templateVarsPromise = fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.MARKETING_TEMPLATE_VARIABLES, "", {
          salonId: businessInfo?.id,
        }),
      ).catch((err) => {
        setApiError(
          "templateVars",
          err.message || "Failed to fetch template variables",
        );
        return null;
      });

      const templatesPromise = fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.MARKETING_TEMPLATES),
      ).catch((err) => {
        setApiError(
          "templates",
          err.message || "Failed to fetch message templates",
        );
        return null;
      });

      const complianceMessagesPromise = fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.MARKETING_COMPLIANCE_MESSAGES),
      ).catch((err) => {
        setApiError(
          "complianceMessages",
          err.message || "Failed to fetch compliance messages",
        );
        return null;
      });

      const [
        templateVarsResponse,
        templatesResponse,
        complianceMessagesResponse,
      ] = await Promise.all([
        templateVarsPromise,
        templatesPromise,
        complianceMessagesPromise,
      ]);

      // Process all responses
      if (templateVarsResponse) {
        setTemplateVariables(templateVarsResponse);
      }

      if (templatesResponse) {
        setMessageTemplates(templatesResponse);
      }

      if (complianceMessagesResponse) {
        setComplianceMessages(complianceMessagesResponse);
      }

      // Mark data as loaded for caching (even with partial success)
      setMarketingDataLoaded(true);
    } catch (err) {
      console.error("Error fetching marketing data:", err);
      // We don't set general errors here as we've handled each specific error above
    } finally {
      // Clear all loading states
      setLoading("templateVars", false);
      setLoading("templates", false);
      setLoading("complianceMessages", false);
    }
  }, [
    businessInfo?.id,
    marketingDataLoaded,
    fetchWithAuth,
    setLoading,
    setApiError,
  ]);

  // Fetch marketing data when the business changes
  useEffect(() => {
    if (businessInfo?.id) {
      fetchMarketingData();
    }
  }, [businessInfo?.id, fetchMarketingData]);

  // Reset marketingDataLoaded when business changes
  useEffect(() => {
    setMarketingDataLoaded(false);
  }, [businessInfo?.id]);

  // Utility: reset all state
  const resetCampaign = useCallback(() => {
    setStep(1);
    setName("");
    setDescription("");
    setErrors({});
    setTouched(false);
    setSelectedClientIds([]);
    setSelectedClientGroupIds([]);
    setClientSearch("");
    setGenderFilter("");
    setAgeGroupFilter("");
    setClientPage(1);
    setSelectedTemplate("");
    setMessage("");
    setIsScheduled(false);
    setScheduledDate(moment().format("YYYY-MM-DD"));
    setScheduledTime("");
    setLoadingState({
      templateVars: false,
      templates: false,
      complianceMessages: false,
    });
    setApiErrors({
      templateVars: null,
      templates: null,
      complianceMessages: null,
    });
    setComplianceLoading(false);
    setComplianceStatus(null);
    setComplianceErrors([]);
    setComplianceWarnings([]);
    setSubmitted(false);
    setShowDiscardModal(false);
    // Don't reset the actual data to maintain the cache
  }, []);

  // Compliance check function
  const checkCompliance = useCallback(async () => {
    if (!businessInfo?.id) return "fail";

    setLoading(true);
    setComplianceStatus(null);
    setComplianceErrors([]);
    setComplianceWarnings([]);

    try {
      const sendTime = isScheduled
        ? moment
            .tz(scheduledDate, businessInfo.timezone)
            .hour(moment(scheduledTime, "h:mm A").hour())
            .minute(moment(scheduledTime, "h:mm A").minute())
            .utc()
            .format()
        : moment().utc().format();

      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.MARKETING_CHECK_COMPLIANCE),
        {
          method: "POST",
          body: JSON.stringify({
            salonId: businessInfo.id,
            campaign_name: name,
            campaign_description: description,
            clients: selectedClientIds,
            ...(clientGroupsEnabled
              ? { client_group_ids: selectedClientGroupIds }
              : {}),
            body: message,
            send_time: sendTime,
          }),
        },
      );

      setComplianceStatus(response.status);
      setComplianceErrors(response.errors || []);
      setComplianceWarnings(response.warnings || []);

      return response.status;
    } catch (error) {
      setComplianceStatus("fail");
      setComplianceErrors([error.message]);
      return "fail";
    } finally {
      setLoading(false);
    }
  }, [
    businessInfo?.id,
    name,
    description,
    selectedClientIds,
    selectedClientGroupIds,
    clientGroupsEnabled,
    message,
    fetchWithAuth,
    isScheduled,
    scheduledDate,
    scheduledTime,
  ]);

  // Create campaign function
  const createCampaign = useCallback(async () => {
    if (!businessInfo?.id) return null;

    setCampaignCreationLoading(true);
    setCampaignCreationError(null);

    try {
      const sendTime = isScheduled
        ? moment
            .tz(scheduledDate, businessInfo.timezone)
            .hour(moment(scheduledTime, "h:mm A").hour())
            .minute(moment(scheduledTime, "h:mm A").minute())
            .utc()
            .format()
        : moment().utc().format();

      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.MARKETING_CAMPAIGNS),
        {
          method: "POST",
          body: JSON.stringify({
            salonId: businessInfo.id,
            campaign_name: name,
            campaign_description: description,
            clients: selectedClientIds,
            ...(clientGroupsEnabled
              ? { client_group_ids: selectedClientGroupIds }
              : {}),
            body: message,
            send_time: sendTime,
          }),
        },
      );

      return response;
    } catch (error) {
      setCampaignCreationError(error.message);
      return null;
    } finally {
      setCampaignCreationLoading(false);
    }
  }, [
    businessInfo?.id,
    name,
    description,
    selectedClientIds,
    selectedClientGroupIds,
    clientGroupsEnabled,
    message,
    fetchWithAuth,
    isScheduled,
    scheduledDate,
    scheduledTime,
  ]);

  // Fetch campaigns by salon ID
  const fetchCampaigns = useCallback(async () => {
    if (!businessInfo?.id) return;

    setCampaignLoading((prev) => ({ ...prev, list: true }));
    setCampaignErrors((prev) => ({ ...prev, list: null }));

    try {
      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SALON_CAMPAIGNS, "", {
          salonId: businessInfo.id,
        }),
      );
      setCampaigns(response);
    } catch (error) {
      setCampaignErrors((prev) => ({ ...prev, list: error.message }));
    } finally {
      setCampaignLoading((prev) => ({ ...prev, list: false }));
    }
  }, [businessInfo?.id, fetchWithAuth]);

  // Fetch campaign details by ID
  const fetchCampaignDetails = useCallback(
    async (campaignId) => {
      setCampaignLoading((prev) => ({ ...prev, details: true }));
      setCampaignErrors((prev) => ({ ...prev, details: null }));

      try {
        const response = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.MARKETING_CAMPAIGN_BY_ID, "", {
            campaignId,
          }),
        );
        setCampaignDetails(response);
        setCampaignMessages(response.messages || []);
      } catch (error) {
        setCampaignErrors((prev) => ({ ...prev, details: error.message }));
      } finally {
        setCampaignLoading((prev) => ({ ...prev, details: false }));
      }
    },
    [fetchWithAuth],
  );

  // Update campaign details
  const updateCampaign = useCallback(
    async (campaignId, campaignName, campaignDescription) => {
      setCampaignLoading((prev) => ({ ...prev, update: true }));
      setCampaignErrors((prev) => ({ ...prev, update: null }));

      try {
        const response = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.UPDATE_CAMPAIGN),
          {
            method: "PUT",
            body: JSON.stringify({
              campaign_id: campaignId,
              campaign_name: campaignName,
              campaign_description: campaignDescription,
            }),
          },
        );

        // Update local state with the response
        if (response) {
          setCampaignDetails((prev) => ({
            ...prev,
            name: response.campaign_name,
            description: response.campaign_description,
          }));
        }

        return response;
      } catch (error) {
        setCampaignErrors((prev) => ({ ...prev, update: error.message }));
        return null;
      } finally {
        setCampaignLoading((prev) => ({ ...prev, update: false }));
      }
    },
    [fetchWithAuth],
  );

  // Delete campaign
  const deleteCampaign = useCallback(
    async (campaignId) => {
      setCampaignLoading((prev) => ({ ...prev, delete: true }));
      setCampaignErrors((prev) => ({ ...prev, delete: null }));

      try {
        const response = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.MARKETING_CAMPAIGN_BY_ID, "", {
            campaignId,
          }),
          {
            method: "DELETE",
          },
        );

        return response;
      } catch (error) {
        setCampaignErrors((prev) => ({ ...prev, delete: error.message }));
        return null;
      } finally {
        setCampaignLoading((prev) => ({ ...prev, delete: false }));
      }
    },
    [fetchWithAuth],
  );

  // Memoized so consumers don't re-render on every provider render; every
  // method is useCallback'd and everything else is state/useMemo, so all
  // non-setter deps are listed below (useState setters are stable).
  const value = useMemo(
    () => ({
      allClients,
      clientsLoading,
      clientsError,
      step,
      setStep,
      name,
      setName,
      description,
      setDescription,
      errors,
      setErrors,
      touched,
      setTouched,
      selectedClientIds,
      setSelectedClientIds,
      selectedClientGroupIds,
      setSelectedClientGroupIds,
      clientSearch,
      setClientSearch,
      genderFilter,
      setGenderFilter,
      ageGroupFilter,
      setAgeGroupFilter,
      clientPage,
      setClientPage,
      sorting,
      setSorting,
      processedClients,
      selectedTemplate,
      setSelectedTemplate,
      message,
      setMessage,
      templateVariables,
      messageTemplates,
      complianceMessages,
      loadingState,
      apiErrors,
      fetchTemplateVariables,
      fetchMessageTemplates,
      fetchComplianceMessages,
      fetchMarketingData,
      complianceLoading,
      setComplianceLoading,
      complianceStatus,
      setComplianceStatus,
      complianceErrors,
      setComplianceErrors,
      complianceWarnings,
      setComplianceWarnings,
      submitted,
      setSubmitted,
      showDiscardModal,
      setShowDiscardModal,
      resetCampaign,
      checkCompliance,
      createCampaign,
      campaignCreationLoading,
      campaignCreationError,
      campaigns,
      campaignDetails,
      campaignMessages,
      campaignLoading,
      campaignErrors,
      fetchCampaigns,
      fetchCampaignDetails,
      updateCampaign,
      deleteCampaign,
      isScheduled,
      setIsScheduled,
      scheduledDate,
      setScheduledDate,
      scheduledTime,
      setScheduledTime,
    }),
    [
      allClients,
      clientsLoading,
      clientsError,
      step,
      name,
      description,
      errors,
      touched,
      selectedClientIds,
      selectedClientGroupIds,
      clientSearch,
      genderFilter,
      ageGroupFilter,
      clientPage,
      sorting,
      processedClients,
      selectedTemplate,
      message,
      templateVariables,
      messageTemplates,
      complianceMessages,
      loadingState,
      apiErrors,
      fetchTemplateVariables,
      fetchMessageTemplates,
      fetchComplianceMessages,
      fetchMarketingData,
      complianceLoading,
      complianceStatus,
      complianceErrors,
      complianceWarnings,
      submitted,
      showDiscardModal,
      resetCampaign,
      checkCompliance,
      createCampaign,
      campaignCreationLoading,
      campaignCreationError,
      campaigns,
      campaignDetails,
      campaignMessages,
      campaignLoading,
      campaignErrors,
      fetchCampaigns,
      fetchCampaignDetails,
      updateCampaign,
      deleteCampaign,
      isScheduled,
      scheduledDate,
      scheduledTime,
    ],
  );

  return (
    <CampaignContext.Provider value={value}>
      {children}
    </CampaignContext.Provider>
  );
}

export function useCampaign() {
  const context = useContext(CampaignContext);
  if (!context) {
    throw new Error("useCampaign must be used within a CampaignProvider");
  }
  return context;
}
